import { useState, type ReactNode } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Address {
  first_name: string;
  last_name: string;
  company?: string;
  address_1: string;
  address_2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email?: string;
  phone?: string;
}

interface AddressCardProps {
  title: string;
  icon?: ReactNode;
  address: Address;
  canEdit: boolean;
  onSave: (address: Address) => Promise<void>;
}

export function AddressCard({ title, icon, address, canEdit, onSave }: AddressCardProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Address>(address);
  const [saving, setSaving] = useState(false);

  function handleEdit() {
    setForm(address);
    setEditing(true);
  }

  function handleCancel() {
    setForm(address);
    setEditing(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(form);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function update(field: keyof Address, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  return (
    <div className="ring-1 ring-foreground/10">
      <div className="flex items-center justify-between px-3 py-2.5 border-b">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-xs font-medium">{title}</span>
        </div>
        {canEdit && !editing && (
          <Button variant="ghost" size="icon-xs" onClick={handleEdit}>
            <Pencil className="h-3 w-3" />
          </Button>
        )}
        {editing && (
          <div className="flex gap-0.5">
            <Button variant="ghost" size="icon-xs" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            </Button>
            <Button variant="ghost" size="icon-xs" onClick={handleCancel} disabled={saving}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
      <div className="p-3">
        {editing ? (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Ime</Label>
                <Input
                  className="h-7 text-xs"
                  value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Prezime</Label>
                <Input
                  className="h-7 text-xs"
                  value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Firma</Label>
              <Input
                className="h-7 text-xs"
                value={form.company || ""}
                onChange={(e) => update("company", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Adresa 1</Label>
              <Input
                className="h-7 text-xs"
                value={form.address_1}
                onChange={(e) => update("address_1", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Adresa 2</Label>
              <Input
                className="h-7 text-xs"
                value={form.address_2 || ""}
                onChange={(e) => update("address_2", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Grad</Label>
              <Input
                className="h-7 text-xs"
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Država/Region</Label>
              <Input
                className="h-7 text-xs"
                value={form.state}
                onChange={(e) => update("state", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Poštanski broj</Label>
                <Input
                  className="h-7 text-xs"
                  value={form.postcode}
                  onChange={(e) => update("postcode", e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Zemlja</Label>
                <Input
                  className="h-7 text-xs"
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                />
              </div>
            </div>
            {("email" in address || form.email !== undefined) && (
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Email</Label>
                <Input
                  className="h-7 text-xs"
                  value={form.email || ""}
                  onChange={(e) => update("email", e.target.value)}
                />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Telefon</Label>
              <Input
                className="h-7 text-xs"
                value={form.phone || ""}
                onChange={(e) => update("phone", e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
            {(address.first_name || address.last_name) && (
              <p className="font-medium text-foreground">
                {address.first_name} {address.last_name}
              </p>
            )}
            {address.company && <p>{address.company}</p>}
            {address.address_1 && <p>{address.address_1}</p>}
            {address.address_2 && <p>{address.address_2}</p>}
            {(address.city || address.postcode) && (
              <p>
                {address.city}{address.city && address.postcode ? ", " : ""}{address.postcode}
              </p>
            )}
            {(address.state || address.country) && (
              <p>
                {address.state}{address.state && address.country ? ", " : ""}{address.country}
              </p>
            )}
            {address.email && <p>{address.email}</p>}
            {address.phone && <p>{address.phone}</p>}
            {!address.first_name && !address.last_name && !address.address_1 && (
              <p className="italic">Nema adrese</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
